import React, { createContext, useContext, useEffect, useState } from 'react';
import moment from 'moment-jalaali';
import { articlesService } from '../../services/api';
import { toFaDigits } from '../../utils/blogFormat';

export interface BlogLink {
  label: string;
  href: string;
}

export interface BlogSettings {
  brandName: string;
  navLinks: BlogLink[];
  loginLabel: string;
  loginHref: string;
  mobileMenuLoginLabel: string;
  heroTitle: string;
  heroSubtitle: string;
  searchPlaceholder: string;
  featuredTitle: string;
  featuredBadge: string;
  featuredBadgeMobile: string;
  allArticlesTitle: string;
  tocTitle: string;
  videoTitle: string;
  tagsTitle: string;
  feedbackTitle: string;
  feedbackSubtitle: string;
  commentsTitle: string;
  commentsSubtitle: string;
  commentsSubtitleMobile: string;
  commentFormTitle: string;
  commentNote: string;
  officialBadge: string;
  officialBadgeMobile: string;
  commentNamePlaceholder: string;
  commentNamePlaceholderMobile: string;
  commentTextPlaceholder: string;
  commentTextPlaceholderMobile: string;
  relatedTitle: string;
  relatedMoreLabel: string;
  footerTagline: string;
  footerTaglineMobileList: string;
  footerTaglineMobileDetail: string;
  copyright: string;
  footerLinks: BlogLink[];
  social: { youtube: string; instagram: string; telegram: string; email: string; podcast: string; rss: string };
}

/** همان پیش‌فرض‌های بک‌اند (متن طرح)؛ تا رسیدن پاسخ API صفحه با این‌ها رندر می‌شود */
export const BLOG_SETTINGS_DEFAULTS: BlogSettings = {
  brandName: 'من حقیقی',
  navLinks: [
    { label: 'خانه', href: '/' },
    { label: 'دوره‌ها', href: '/courses' },
    { label: 'مقالات', href: '/articles' },
    { label: 'درباره ما', href: '/about' },
    { label: 'تماس با ما', href: '/contact' },
  ],
  loginLabel: 'ورود',
  loginHref: '/login',
  mobileMenuLoginLabel: 'ورود به حساب کاربری',
  heroTitle: 'مجله من حقیقی',
  heroSubtitle: 'مقاله‌هایی برای شناخت خود و ساختن زندگی آگاهانه‌تر',
  searchPlaceholder: 'عنوان یا موضوع موردنظرت را جست‌وجو کن...',
  featuredTitle: 'منتخب من حقیقی',
  featuredBadge: 'پیشنهاد تحریریه',
  featuredBadgeMobile: 'گزینش تحریریه',
  allArticlesTitle: 'همه مقالات',
  tocTitle: 'در این مقاله می‌خوانید',
  videoTitle: 'ویدیوی مرتبط با این مقاله',
  tagsTitle: 'برچسب‌های موضوعی:',
  feedbackTitle: 'آیا این مقاله برای شما مفید بود؟',
  feedbackSubtitle: 'بازخورد شما به بهبود کیفیت مقالات بعدی کمک می‌کند.',
  commentsTitle: 'دیدگاه‌ها',
  commentsSubtitle: 'پاسخ‌ها و گفتگوهای اعضا',
  commentsSubtitleMobile: 'پاسخ‌های تأییدشده',
  commentFormTitle: 'دیدگاه شما',
  commentNote: 'دیدگاه شما پس از بررسی منتشر می‌شود.',
  officialBadge: 'تیم من حقیقی',
  officialBadgeMobile: 'پاسخ رسمی',
  commentNamePlaceholder: 'نام خود را وارد کنید…',
  commentNamePlaceholderMobile: 'مثلاً: مریم یا ناشناس',
  commentTextPlaceholder: 'دیدگاه، تجربه یا پرسش خود را درباره این مقاله بنویسید…',
  commentTextPlaceholderMobile: 'اندیشه‌ها یا تجربیات خود را درباره این موضوع بنویسید...',
  relatedTitle: 'مقالات مرتبط',
  relatedMoreLabel: 'مشاهده همه مقالات',
  footerTagline: 'مجله‌ای برای یادگیری، آگاهی و تجربه زیست اصیل',
  footerTaglineMobileList: 'رسانه‌ای مستقل برای آموزش درون‌نگری، رشد فردی و روانشناسی کاربردی در زندگی مدرن.',
  footerTaglineMobileDetail: 'مجله تامل، خودشناسی و کشف ساختارهای ناخودآگاه فردی و جمعی',
  copyright: 'تمامی حقوق محفوظ است © {year} مجله من حقیقی',
  footerLinks: [
    { label: 'حریم خصوصی', href: '/privacy' },
    { label: 'شرایط استفاده', href: '/terms' },
    { label: 'درباره ما', href: '/about' },
    { label: 'تماس با ما', href: '/contact' },
    { label: 'همکاری در نگارش', href: '/partnership' },
  ],
  social: {
    youtube: '',
    instagram: '',
    telegram: '',
    email: 'mailto:support@manhaghighi.com',
    podcast: '/podcasts',
    rss: '/api/articles/feed',
  },
};

/** یک درخواست مشترک برای همهٔ صفحه‌های وبلاگ در طول بازدید */
let settingsPromise: Promise<BlogSettings> | null = null;

const loadSettings = (): Promise<BlogSettings> => {
  if (!settingsPromise) {
    settingsPromise = articlesService
      .getSettings()
      .then((data) => ({
        ...BLOG_SETTINGS_DEFAULTS,
        ...data,
        social: { ...BLOG_SETTINGS_DEFAULTS.social, ...(data?.social || {}) },
      }))
      .catch(() => {
        settingsPromise = null;
        return BLOG_SETTINGS_DEFAULTS;
      });
  }
  return settingsPromise;
};

const BlogSettingsContext = createContext<BlogSettings>(BLOG_SETTINGS_DEFAULTS);

export const BlogSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<BlogSettings>(BLOG_SETTINGS_DEFAULTS);
  useEffect(() => {
    let alive = true;
    loadSettings().then((s) => alive && setSettings(s));
    return () => {
      alive = false;
    };
  }, []);
  return <BlogSettingsContext.Provider value={settings}>{children}</BlogSettingsContext.Provider>;
};

export const useBlogSettings = () => useContext(BlogSettingsContext);

/** برای متن‌هایی که طرح در دسکتاپ و موبایل متفاوت نوشته (مثل placeholder که با CSS عوض نمی‌شود) */
export const useIsDesktop = () => {
  const query = '(min-width: 1024px)';
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return matches;
};

/** متن کپی‌رایت با سال شمسی جاری به‌جای {year} */
export const formatCopyright = (text: string) => text.replace('{year}', toFaDigits(moment().jYear()));

/** لینک داخلی (Link روتر) یا بیرونی (a) */
export const isInternalHref = (href: string) => href.startsWith('/') && !href.startsWith('//') && !href.startsWith('/api/');
